import { normalizeText } from './menuDrinks';

/**
 * PROFILS D'OPTIONS PAR CATÉGORIE — modèle « Big Burger » (legacy prod).
 *
 * Objectif (chantier onboarding) : sur un nouveau site, appliquer d'office le bon
 * jeu d'options — DANS LE BON ORDRE — à chaque produit selon le NOM de sa catégorie,
 * au lieu de tout cocher à la main. « Les options sont généralement les mêmes par
 * catégorie » (Ahmed).
 *
 * Source : mapping `category_id → [options ordonnées]` extrait du legacy Big Burger
 * (deploy_bigburger prod v42, fonction d'assignation d'options par catégorie).
 * Legacy (ordre exact) :
 *   burgers / burgers-signature : format, sauces, supplements, boisson_menu, boissons_extra, dessert
 *   sandwichs                   : pain_galette, format, sauces, supplements, boisson_menu, boissons_extra, dessert
 *   hummers                     : format, sauces, boisson_menu, boissons_extra, dessert
 *   tacos                       : viande, sauces, extras(→supplements), boissons_extra, dessert
 *   paninis                     : format, sauces, boissons_extra, dessert
 *   tex-mex / cheese            : sauce(→sauces), boissons_extra, dessert
 *   assiettes                   : viande, format, sauces, boissons_extra, dessert
 *   boissons                    : (le produit EST la boisson → aucune option de garniture)
 *
 * Les slugs ci-dessous sont ceux de la bibliothèque d'options SaaS par défaut
 * (defaultOptions.ts) + `boisson_menu` (option boisson au menu auto). L'applicateur
 * ne garde QUE les slugs réellement présents dans la bibliothèque du resto (filtrage),
 * donc un profil qui cite un slug absent ne casse rien : il est simplement ignoré.
 */

/** Un profil = liste ORDONNÉE de slugs d'options à appliquer aux produits d'une catégorie. */
export type CategoryOptionProfile = string[];

export const CATEGORY_OPTION_PROFILES: Record<string, CategoryOptionProfile> = {
  burger: ['format', 'sauces', 'supplements', 'boisson_menu', 'boissons_extra', 'dessert_suggestion'],
  sandwich: ['pain_galette', 'format', 'sauces', 'supplements', 'boisson_menu', 'boissons_extra', 'dessert_suggestion'],
  hummer: ['format', 'sauces', 'boisson_menu', 'boissons_extra', 'dessert_suggestion'],
  tacos: ['viande_tacos', 'sauces', 'supplements', 'boisson_menu', 'boissons_extra', 'dessert_suggestion'],
  panini: ['format', 'sauces', 'boissons_extra', 'dessert_suggestion'],
  texmex: ['sauces', 'boissons_extra', 'dessert_suggestion'],
  assiette: ['viande_tacos', 'format', 'sauces', 'boissons_extra', 'dessert_suggestion'],
  wrap: ['viande_tacos', 'crudites', 'sauces', 'boisson_menu', 'boissons_extra', 'dessert_suggestion'],
  bowl: ['viande_tacos', 'sauces', 'boissons_extra', 'dessert_suggestion'],
  brochette: ['sauces', 'supplements', 'boissons_extra', 'dessert_suggestion'],
  salade: ['sauces', 'boissons_extra', 'dessert_suggestion'],
  barquette: ['sauces', 'boissons_extra'],
  kids: ['boisson_menu', 'dessert_suggestion'],
  dessert: [],
  boisson: [],
  supplement: [],
};

/**
 * Associe un NOM de catégorie (libre, saisi par le resto) à une clé de profil.
 * Normalisé (accents/casse) + synonymes/pluriels FR courants. Retourne `null`
 * si aucune correspondance (→ pas d'application auto, on laisse la catégorie vide).
 */
export function matchCategoryProfileKey(categoryName: string | null | undefined): string | null {
  const n = normalizeText(categoryName);
  if (!n) return null;
  // Ordre important : les motifs les plus spécifiques d'abord.
  if (/burger/.test(n)) return 'burger';
  if (/sandwich|sandwish/.test(n)) return 'sandwich';
  if (/hummer|hammer|smash/.test(n)) return 'hummer';
  if (/tacos|\btac\b|\btacs\b/.test(n)) return 'tacos';
  if (/panini/.test(n)) return 'panini';
  if (/tex.?mex|nachos|tenders|wings|chicken/.test(n)) return 'texmex';
  if (/assiette/.test(n)) return 'assiette';
  if (/wrap/.test(n)) return 'wrap';
  if (/bowl/.test(n)) return 'bowl';
  if (/brochette/.test(n)) return 'brochette';
  if (/salade|salad/.test(n)) return 'salade';
  if (/barquette|frite/.test(n)) return 'barquette';
  if (/kids|enfant/.test(n)) return 'kids';
  if (/dessert|tiramisu|glace|donut/.test(n)) return 'dessert';
  if (/boisson|drink|soft|soda|canette|bouteille/.test(n)) return 'boisson';
  if (/supplement|extra/.test(n)) return 'supplement';
  return null;
}

/** Slugs d'options (ordonnés) recommandés pour une catégorie donnée (ou [] si inconnue). */
export function optionSlugsForCategory(categoryName: string | null | undefined): string[] {
  const key = matchCategoryProfileKey(categoryName);
  return key ? CATEGORY_OPTION_PROFILES[key] ?? [] : [];
}

/**
 * Planifie les IDs d'options à affecter à un produit selon sa catégorie :
 * on prend le profil de la catégorie, puis on ne garde QUE les slugs réellement
 * présents dans la bibliothèque du resto (map slug→id), en préservant l'ordre.
 * Retourne [] si la catégorie n'a pas de profil ou si aucun slug n'est disponible.
 */
export function plannedOptionIdsForCategory(
  categoryName: string | null | undefined,
  slugToId: Record<string, string>,
): string[] {
  return optionSlugsForCategory(categoryName)
    .map((slug) => slugToId[slug])
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}
